import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withFirebase } from '../Firebase';
import { useChatbotStore } from '../../store/chatbot.store';
import { useExcludedChipsStore } from '../../store/excludedChips.store';
import {
  TextField,
  Chip,
  Button,
  Typography,
  makeStyles,
  Divider,
  IconButton,
  Collapse
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CategorizedAutocomplete from '../CategorizedAutocomplete';
import { TAGSPATH, GENRESPATH, getOrgCollectionPath } from '../../constants/paths';
import { useOrganizationStore } from '../../store/organization.store';
import moment from 'moment';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import '../../assets/scss/_chip-styling.scss';
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@material-ui/core';
import { collection, getDocs } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    backgroundColor: 'transparent',
    borderRadius: 4,
    marginBottom: theme.spacing(1),
    fontFamily: 'Poppins, sans-serif',
    width: '100%',
    maxWidth: '300px'
  },
  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.spacing(0.5, 0),
    '&:hover': {
      opacity: 0.8
    }
  },
  filterContent: {
    marginTop: theme.spacing(0.5)
  },
  autocomplete: {
    width: '100%',
    '& .MuiInputBase-root': {
      backgroundColor: 'transparent',
      color: '#fff',
      minHeight: '32px',
      fontFamily: 'var(--font-family-primary)',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
        borderWidth: '1px'
      }
    },
    '& input': {
      padding: '4px 8px',
      fontSize: 'var(--menu-font-size)',
      fontFamily: 'var(--font-family-primary)'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.1)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.2)'
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      color: '#ffffff',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
      }
    },
    '& .MuiAutocomplete-popper': {
      '& .MuiPaper-root': {
      color: '#fff'
    },
      '& .MuiAutocomplete-option': {
        fontSize: 'var(--menu-font-size)',
        fontFamily: 'var(--font-family-primary)',
        padding: '8px 16px',
        '&[data-focus="true"]': {
          backgroundColor: 'rgba(255, 255, 255, 0.02)'
        },
        '&[aria-selected="true"]': {
          backgroundColor: 'var(--accent-1-color)'
        }
      }
    }
  },
  chip: {
    backgroundColor: 'var(--accent-1-color)',
    color: '#fff',
    margin: theme.spacing(0.25),
    height: '20px',
    fontFamily: 'Poppins, sans-serif',
    '& .MuiChip-label': {
      padding: '0 6px',
      fontSize: '0.75rem',
      fontFamily: 'Poppins, sans-serif'
    },
    '& .MuiChip-deleteIcon': {
      color: '#fff',
      fontSize: '16px',
      margin: '0 2px 0 -4px'
    }
  },
  stateDisplay: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#2a2a2a',
    borderRadius: 4
  },
  buttonContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(2),
    '& > button': {
      minWidth: '80px',
      padding: '2px 8px',
      minHeight: '24px',
      fontSize: '0.75rem',
      margin: theme.spacing(0.25),
      zIndex: 1,
      boxShadow: 'none',
      '&:not(:last-child)': {  // This targets all buttons except the last one (Clear button)
        display: 'none'
      }
    }
  },
  radioGroup: {
    marginTop: 0,
    '& .MuiFormControlLabel-label': {
      color: '#fff',
      fontSize: '0.75rem',
      fontFamily: 'Poppins, sans-serif'
    },
    '& .MuiRadio-root': {
      padding: '4px',
      '& .MuiSvgIcon-root': {
        fontSize: '1.25rem'
      }
    }
  },
  paper: {
    padding: theme.spacing(1),
    backgroundColor: '#1a1a1a',
    borderRadius: 4,
    marginBottom: theme.spacing(1)
  },
  stats: {
    marginBottom: theme.spacing(1)
  },
  divider: {
    margin: theme.spacing(1, 0),
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  },
  filterSection: {
    marginBottom: theme.spacing(1)
  },
  trackNameInput: {
    '& input': {
      color: 'white',
      backgroundColor: 'transparent',
      minHeight: '32px',
      fontFamily: 'Poppins',
      padding: '4px 8px',
      fontSize: 'var(--menu-font-size)'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.1)'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2)'
      }
    }
  }
}));

const LOGIC_TYPES = {
  AND: 'and',
  OR: 'or'
};

const FilterSection = ({ title, icon, children, defaultExpanded = false, selectedCount = 0, isExpanded, onExpandChange }) => {
  const classes = useStyles();
  const [localExpanded, setLocalExpanded] = useState(defaultExpanded);

  const expanded = Boolean(isExpanded !== undefined ? isExpanded : localExpanded);
  const handleExpandChange = () => {
    if (onExpandChange) {
      onExpandChange(!expanded);
    } else {
      setLocalExpanded(!expanded);
    }
  };

  const hasActive = selectedCount > 0;

  return (
    <div className={classes.filterSection}>
      <div
        className={classes.filterHeader}
        onClick={handleExpandChange}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          {icon && (
            <i className={icon} style={{
              fontSize: '13px',
              width: '20px',
              textAlign: 'center',
              flexShrink: 0,
              color: hasActive ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.4)',
            }}></i>
          )}
          <Typography
            variant="subtitle1"
            className="filter-section-label"
            style={{
              color: hasActive ? 'var(--accent-1-color)' : '#fff',
              fontSize: '0.875rem',
              textTransform: 'none',
              fontFamily: 'Poppins, sans-serif',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}{hasActive ? ` (${selectedCount})` : ''}
          </Typography>
        </div>
        <IconButton size="small" className="filter-section-chevron" style={{ color: 'rgba(255, 255, 255, 0.3)', flexShrink: 0, padding: '4px' }}>
          {expanded ? <ExpandLess style={{ fontSize: '16px' }} /> : <ExpandMore style={{ fontSize: '16px' }} />}
        </IconButton>
      </div>
      <Collapse in={expanded}>
        <div
          className={`${classes.filterContent} filter-section-content`}
          onClick={(e) => {}}
        >
          {children}
        </div>
      </Collapse>
      <Divider className={classes.divider} />
    </div>
  );
};

// Define the fixed order for filters
const FILTER_ORDER = [
  'trackName',
  'tags',
  'tags2',
  'genres',
  'keys',
  'bpm',
  'date',
  'license',
  'composers',
  'sessionArtists',
  'clients',
  'agencies',
  'producers',
  'creativeDirectors',
  'studios'
];

const TestMUITags = ({ firebase, onFiltersChange, initialFilterState, onMnemonicReset, onLicensedReset, mode }) => {
  const classes = useStyles();
  const isPublicMode = mode === 'public';
  // Use organization-level role instead of global auth role
  const currentOrgRole = useOrganizationStore((state) => state.currentOrgRole);
  const isSuperAdmin = useOrganizationStore((state) => state.isSuperAdmin);
  // Super admins get ADMIN role, otherwise use org role (default to USER if not set)
  // Public mode always uses USER-level filter restrictions
  const role = isPublicMode ? 'USER' : (isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER'));
  const addFilterResetFlag = useChatbotStore((state) => state.addFilterResetFlag);
  const currentOrgId = useOrganizationStore((state) => state.currentOrgId);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedTags2, setSelectedTags2] = useState([]);
  const [selectedCreativeDirectors, setSelectedCreativeDirectors] = useState([]);
  const [selectedSessionArtists, setSelectedSessionArtists] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedStudios, setSelectedStudios] = useState([]);
  const [selectedProducers, setSelectedProducers] = useState([]);
  const [selectedComposers, setSelectedComposers] = useState([]);
  const [selectedBPM, setSelectedBPM] = useState(null);
  const [bpmOptions, setBpmOptions] = useState([]);
  const [tagLogicType, setTagLogicType] = useState(LOGIC_TYPES.OR);
  const [tagLogicType2, setTagLogicType2] = useState(LOGIC_TYPES.OR);
  
  const [creativeDirectorLogicType, setCreativeDirectorLogicType] = useState(LOGIC_TYPES.OR);
  const [sessionArtistLogicType, setSessionArtistLogicType] = useState(LOGIC_TYPES.OR);
  const [genreLogicType, setGenreLogicType] = useState(LOGIC_TYPES.OR);
  const [agencyLogicType, setAgencyLogicType] = useState(LOGIC_TYPES.OR);
  const [clientLogicType, setClientLogicType] = useState(LOGIC_TYPES.OR);
  const [keyLogicType, setKeyLogicType] = useState(LOGIC_TYPES.OR);
  const [studioLogicType, setStudioLogicType] = useState(LOGIC_TYPES.OR);
  const [producerLogicType, setProducerLogicType] = useState(LOGIC_TYPES.OR);
  const [composerLogicType, setComposerLogicType] = useState(LOGIC_TYPES.OR);
  const [availableTags, setAvailableTags] = useState([]);
  const [availableCreativeDirectors, setAvailableCreativeDirectors] = useState([]);
  const [availableSessionArtists, setAvailableSessionArtists] = useState([]);
  const [availableGenres, setAvailableGenres] = useState([]);
  const [availableAgencies, setAvailableAgencies] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [availableKeys, setAvailableKeys] = useState([]);
  const [availableStudios, setAvailableStudios] = useState([]);
  const [availableProducers, setAvailableProducers] = useState([]);
  const [availableComposers, setAvailableComposers] = useState([]);
  const [trackNameSearch, setTrackNameSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    trackName: false,
    tags: false,
    tags2: false,
    genres: false,
    bpm: false,
    keys: false,
    creativeDirectors: false,
    sessionArtists: false,
    agencies: false,
    clients: false,
    studios: false,
    producers: false,
    composers: false,
    date: false,
    license: false
  });
  const [dateFilterType, setDateFilterType] = useState('completed');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [licenseFilter, setLicenseFilter] = useState('all');
  const [bpmFilterType, setBpmFilterType] = useState('exact');
  const [bpmRange, setBpmRange] = useState({ min: '', max: '' });
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
  const [isInitialFilterStateUpdate, setIsInitialFilterStateUpdate] = useState(false);
  const prevFiltersRef = useRef(JSON.stringify({}));
  const manuallyExpandedSectionsRef = useRef({});
  
  // Use excluded chips store
  const { 
    excludedChips, 
    setChipExcluded, 
    clearAllExcludedChips 
  } = useExcludedChipsStore();

  // Helper function to generate unique chip key
  const getChipKey = useCallback((chip, section) => {
    return `${section}_${chip.id || chip.name}`;
  }, []);

  // Handle toggling chip between included/excluded
  const handleChipToggle = useCallback((chip, section) => {
    const chipKey = getChipKey(chip, section);
    const currentlyExcluded = excludedChips[chipKey];
    
    
    // Set flag to prevent immediate filter emission and Autocomplete changes
    setIsTogglingChip(true);
    
    // Toggle the chip and store its data if excluding
    setChipExcluded(chipKey, !currentlyExcluded, !currentlyExcluded ? chip : null);
    
    // Reset flag after a longer delay to ensure everything stabilizes
    setTimeout(() => {
      setIsTogglingChip(false);
    }, 500);
  }, [getChipKey, excludedChips, setChipExcluded]);

  // Handle removing chip entirely
  const handleChipRemove = useCallback((chip, section, selectedItems, setSelectedItems) => {
    const chipKey = getChipKey(chip, section);
    
    // Remove from selected items
    setSelectedItems(prev => prev.filter(item => item.id !== chip.id));
    
    // Remove from excluded chips
    setChipExcluded(chipKey, false);
  }, [getChipKey, setChipExcluded]);

  // Handle adding chip directly in exclude mode from dropdown
  const handleDirectExclude = useCallback((option, section, selectedItems, setSelectedItems) => {
    const chipKey = getChipKey(option, section);
    
    // Check if chip is already selected
    const isAlreadySelected = selectedItems.some(item => item.id === option.id);
    
    if (!isAlreadySelected) {
      // Add to selected items first
      setSelectedItems(prev => [...prev, option]);
    }
    
    // Set as excluded in store
    setChipExcluded(chipKey, true, option);
    
  }, [getChipKey, setChipExcluded]);

  const loadBPMs = useCallback(async () => {
    try {
      // console.log('Loading BPMs from: bpm');
      const bpmRef = collection(firebase.db, 'bpm');
      const snapshot = await getDocs(bpmRef);
      // console.log('BPMs snapshot received, number of documents:', snapshot.size);
      
      const bpmData = [];
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Use the label field which contains the actual BPM number
        const bpmValue = parseInt(data.label);
        bpmData.push({
          id: doc.id,
          label: bpmValue.toString(),
          name: bpmValue.toString(),
          value: bpmValue
        });
      });

      // Sort BPMs numerically
      const sortedBpmData = bpmData.sort((a, b) => a.value - b.value);
      setBpmOptions(sortedBpmData);
      // console.log('Loaded BPM options:', sortedBpmData);
    } catch (error) {
      // console.error('Error loading BPMs:', error);
    }
  }, [firebase]);

  // Helper function to get options for a specific dropdown type
  const getOptionsForType = useCallback((type) => {
    switch (type) {
      case 'tags':
        return availableTags;
      case 'genres':
        return availableGenres;
      case 'keys':
        return availableKeys;
      case 'creativeDirectors':
        return availableCreativeDirectors;
      case 'sessionArtists':
        return availableSessionArtists;
      case 'agencies':
        return availableAgencies;
      case 'clients':
        return availableClients;
      case 'studios':
        return availableStudios;
      case 'producers':
        return availableProducers;
      case 'composers':
        return availableComposers;
      default:
        return [];
    }
  }, [
    availableTags,
    availableGenres,
    availableKeys,
    availableCreativeDirectors,
    availableSessionArtists,
    availableAgencies,
    availableClients,
    availableStudios,
    availableProducers,
    availableComposers
  ]);

  // Handle keyboard navigation for dropdowns
  const handleDropdownKeyDown = useCallback((event) => {
    const activeElement = document.activeElement;
    
    // Always allow spaces in the track name search field
    if (event.key === ' ') {
      const isTrackNameInput = activeElement?.classList?.contains(classes.trackNameInput) || 
                              activeElement?.closest(`.${classes.trackNameInput}`);
      if (isTrackNameInput) {
        // Prevent the event from bubbling up to avoid triggering other handlers
        event.stopPropagation();
        return;
      }
    }

    // Only handle keyboard events if a dropdown is focused
    if (!isDropdownFocused) return;

    // Prevent default behavior for arrow keys and enter when a dropdown is focused
    if (['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }

    switch (event.key) {
      case 'ArrowUp':
        if (activeOptionIndex > 0) {
          setActiveOptionIndex(prev => prev - 1);
        }
        break;
      case 'ArrowDown':
        if (activeOptionIndex < selectedTags.length - 1) {
          setActiveOptionIndex(prev => prev + 1);
        }
        break;
      case 'Enter':
        if (activeDropdown && activeOptionIndex >= 0) {
          const option = selectedTags[activeOptionIndex];
          if (option) {
            const isSelected = selectedTags.some(tag => tag.id === option.id);
            if (isSelected) {
              setSelectedTags(prev => prev.filter(tag => tag.id !== option.id));
            } else {
              setSelectedTags(prev => [...prev, option]);
            }
          }
        }
        break;
      case 'Escape':
        setIsDropdownFocused(false);
        setActiveDropdown(null);
        setActiveOptionIndex(-1);
        break;
      default:
        break;
    }
  }, [isDropdownFocused, activeDropdown, activeOptionIndex, selectedTags, classes.trackNameInput]);

  // Add keyboard event listener
  useEffect(() => {
    window.addEventListener('keydown', handleDropdownKeyDown);
    return () => {
      window.removeEventListener('keydown', handleDropdownKeyDown);
    };
  }, [handleDropdownKeyDown]);

  // Handle Tab key to focus on dropdowns
  const handleTabKey = useCallback((event) => {
    if (event.key === 'Tab') {
      const activeElement = document.activeElement;
      if (activeElement?.classList?.contains('MuiAutocomplete-input')) {
        setIsDropdownFocused(true);
        const dropdownType = activeElement.closest('.MuiAutocomplete-root')?.dataset?.type;
        if (dropdownType) {
          setActiveDropdown({
            type: dropdownType,
            options: getOptionsForType(dropdownType)
          });
          setActiveOptionIndex(0);
        }
      } else {
        setIsDropdownFocused(false);
        setActiveDropdown(null);
        setActiveOptionIndex(-1);
      }
    }
  }, [getOptionsForType]);

  useEffect(() => {
    window.addEventListener('keydown', handleTabKey);
    return () => {
      window.removeEventListener('keydown', handleTabKey);
    };
  }, [handleTabKey]);

  // Define which filters are visible to which roles
  const filterVisibility = {
    trackName: true, // Visible to all roles
    tags: true, // Visible to all roles
    tags2: true, // Visible to all roles
    genres: true, // Visible to all roles
    bpm: true, // Visible to all roles
    keys: true, // Visible to all roles
    creativeDirectors: ['ADMIN', 'OPERATOR'].includes(role),
    sessionArtists: ['ADMIN', 'OPERATOR'].includes(role),
    agencies: ['ADMIN', 'OPERATOR'].includes(role),
    clients: ['ADMIN', 'OPERATOR'].includes(role),
    studios: ['ADMIN', 'OPERATOR'].includes(role),
    producers: ['ADMIN', 'OPERATOR'].includes(role),
    composers: ['ADMIN', 'OPERATOR'].includes(role),
    date: ['ADMIN', 'OPERATOR'].includes(role),
    license: ['ADMIN', 'OPERATOR'].includes(role)
  };

  useEffect(() => {
    let isMounted = true;

    const loadOptions = async () => {
      try {
        if (!isMounted) return;

        // Load tags (global collection)
        const tagsRef = collection(firebase.db, TAGSPATH);
        const tagsSnapshot = await getDocs(tagsRef);
        if (!isMounted) return;
        const tagsData = [];
        tagsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          tagsData.push({
            id: doc.id,
            name: data.label,
            value: data.value,
            label: data.label
          });
        });
        tagsData.sort((a, b) => a.name.localeCompare(b.name));
        if (isMounted) {
          setAvailableTags(tagsData);
        }

        // Load creative directors (org-scoped collection)
        if (!isMounted) return;
        const cdPath = currentOrgId ? getOrgCollectionPath(currentOrgId, 'creativedirector') : 'creativedirector';
        const cdRef = collection(firebase.db, cdPath);
        const cdSnapshot = await getDocs(cdRef);
        if (!isMounted) return;
        const cdData = [];
        cdSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const cdName = data.name || data.label || data.text || 'Unnamed Creative Director';
          cdData.push({
            id: doc.id,
            name: cdName,
            rawData: data
          });
        });
        cdData.sort((a, b) => a.name.localeCompare(b.name));
        if (isMounted) {
          setAvailableCreativeDirectors(cdData);
        }

        // Load session artists (org-scoped collection)
        if (!isMounted) return;
        const saPath = currentOrgId ? getOrgCollectionPath(currentOrgId, 'sessionartists') : 'sessionartists';
        const saRef = collection(firebase.db, saPath);
        const saSnapshot = await getDocs(saRef);
        if (!isMounted) return;
        const saData = [];
        saSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const saName = data.name || data.label || data.text || 'Unnamed Session Artist';
          saData.push({
            id: doc.id,
            name: saName,
            rawData: data
          });
        });
        saData.sort((a, b) => a.name.localeCompare(b.name));
        if (isMounted) {
          setAvailableSessionArtists(saData);
        }

        // Load genres (global collection)
        if (!isMounted) return;
        const genresRef = collection(firebase.db, GENRESPATH);
        const genresSnapshot = await getDocs(genresRef);
        if (!isMounted) return;
        const genresData = [];
        genresSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const genreName = data.name || data.label || data.text || 'Unnamed Genre';
          genresData.push({
            id: doc.id,
            name: genreName,
            rawData: data
          });
        });
        genresData.sort((a, b) => a.name.localeCompare(b.name));
        if (isMounted) {
          setAvailableGenres(genresData);
        }

        // Load agencies (global collection)
        if (!isMounted) return;
        const agencyPath = 'agency';
        const agencyRef = collection(firebase.db, agencyPath);
        const agencySnapshot = await getDocs(agencyRef);
        if (!isMounted) return;
        const agencyData = [];
        agencySnapshot.docs.forEach(doc => {
          const data = doc.data();
          const agencyName = data.name || data.label || data.text || 'Unnamed Agency';
          agencyData.push({
            id: doc.id,
            name: agencyName,
            rawData: data
          });
        });
        agencyData.sort((a, b) => a.name.localeCompare(b.name));
        if (isMounted) {
          setAvailableAgencies(agencyData);
        }

        // Load clients (global collection)
        if (!isMounted) return;
        const clientPath = 'client';
        const clientRef = collection(firebase.db, clientPath);
        const clientSnapshot = await getDocs(clientRef);
        if (!isMounted) return;
        const clientData = [];
        clientSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const clientName = data.name || data.label || data.text || 'Unnamed Client';
          clientData.push({
            id: doc.id,
            name: clientName,
            rawData: data
          });
        });
        clientData.sort((a, b) => a.name.localeCompare(b.name));
        if (isMounted) {
          setAvailableClients(clientData);
        }

        // Load keys (global collection)
        if (!isMounted) return;
        const keysRef = collection(firebase.db, 'keys');
        const keysSnapshot = await getDocs(keysRef);
        if (!isMounted) return;
        const keysData = [];
        keysSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const keyName = data.name || data.label || data.text || 'Unnamed Key';
          keysData.push({
            id: doc.id,
            name: keyName,
            rawData: data
          });
        });
        keysData.sort((a, b) => a.name.localeCompare(b.name));
        if (isMounted) {
          setAvailableKeys(keysData);
        }

        // Load studios (org-scoped collection)
        if (!isMounted) return;
        const studioPath = currentOrgId ? getOrgCollectionPath(currentOrgId, 'studio') : 'studio';
        const studioRef = collection(firebase.db, studioPath);
        const studioSnapshot = await getDocs(studioRef);
        if (!isMounted) return;
        const studioData = [];
        studioSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const studioName = data.name || data.label || data.text || 'Unnamed Studio';
          studioData.push({
            id: doc.id,
            name: studioName,
            rawData: data
          });
        });
        studioData.sort((a, b) => a.name.localeCompare(b.name));
        if (isMounted) {
          setAvailableStudios(studioData);
        }

        // Load producers (org-scoped collection)
        if (!isMounted) return;
        const producerPath = currentOrgId ? getOrgCollectionPath(currentOrgId, 'producer') : 'producer';
        const producerRef = collection(firebase.db, producerPath);
        const producerSnapshot = await getDocs(producerRef);
        if (!isMounted) return;
        const producerData = [];
        producerSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const producerName = data.name || data.label || data.text || 'Unnamed Producer';
          producerData.push({
            id: doc.id,
            name: producerName,
            rawData: data
          });
        });
        producerData.sort((a, b) => a.name.localeCompare(b.name));
        if (isMounted) {
          setAvailableProducers(producerData);
        }

        // Load composers (org-scoped collection)
        if (!isMounted) return;
        const composersPath = currentOrgId ? getOrgCollectionPath(currentOrgId, 'composers') : 'composers';
        const composersRef = collection(firebase.db, composersPath);
        const composersSnapshot = await getDocs(composersRef);
        if (!isMounted) return;
        const composersData = [];
        composersSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const composerName = data.name || data.label || data.text || 'Unnamed Composer';
          composersData.push({
            id: doc.id,
            name: composerName,
            rawData: data
          });
        });
        composersData.sort((a, b) => a.name.localeCompare(b.name));
        if (isMounted) {
          setAvailableComposers(composersData);
        }

        await loadBPMs();
      } catch (error) {
        // Handle error if needed
      }
    };
    loadOptions();
    return () => {
      isMounted = false;
    };
  }, [firebase, loadBPMs, currentOrgId]);

  useEffect(() => {
    let timeoutId;
    const hasAnyAvailableItems = availableTags.length > 0 || availableGenres.length > 0 || availableKeys.length > 0 || availableStudios.length > 0 || availableAgencies.length > 0 || availableClients.length > 0 || availableProducers.length > 0 || availableComposers.length > 0 || availableCreativeDirectors.length > 0 || availableSessionArtists.length > 0;
    
    if (initialFilterState && hasAnyAvailableItems) {
      setIsInitialFilterStateUpdate(true);
      // Update expanded sections if provided and if this section hasn't been manually expanded
      if (initialFilterState.expandedSections) {
        setExpandedSections(prevState => {
          const newState = { ...prevState };
          
          // Only update sections that haven't been manually expanded
          Object.keys(initialFilterState.expandedSections).forEach(section => {
            if (!manuallyExpandedSectionsRef.current[section]) {
              newState[section] = initialFilterState.expandedSections[section];
            } else {
              // console.log(`[TestMUITags] Preserving manually expanded section: ${section}`);
            }
          });
          
          return newState;
        });
      }

      // Update excluded chips state from filterState
      // Start fresh - clear all excluded chips and rebuild from current state
      const newExcludedChips = {};
      
      // Process excluded tags from both possible structures
      // 1. From excludedTags.data structure (from filter state)
      if (initialFilterState.excludedTags?.data && Array.isArray(initialFilterState.excludedTags.data)) {
        initialFilterState.excludedTags.data.forEach(tag => {
          // Find the matching tag in availableTags to get the correct id
          const matchingTag = availableTags.find(t => 
            (t.name && t.name.toLowerCase() === (tag.label || '').toLowerCase()) ||
            (t.label && t.label.toLowerCase() === (tag.label || '').toLowerCase()) ||
            t.id === tag.value
          );
          if (matchingTag) {
            const chipKey = getChipKey(matchingTag, 'tags');
            newExcludedChips[chipKey] = true;
          }
        });
      }
      
      // 2. From tags.excluded structure (from initial filter state)
      if (initialFilterState.tags?.excluded && Array.isArray(initialFilterState.tags.excluded)) {
        initialFilterState.tags.excluded.forEach(tag => {
          // Find the matching tag in availableTags to get the correct id
          const matchingTag = availableTags.find(t => 
            (t.name && t.name.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
            (t.label && t.label.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
            t.id === (tag.id || tag.value)
          );
          if (matchingTag) {
            const chipKey = getChipKey(matchingTag, 'tags');
            newExcludedChips[chipKey] = true;
          }
        });
      }
      
      // Process excluded tags2 from both possible structures
      // 1. From excludedTags2.data structure (from filter state)
      if (initialFilterState.excludedTags2?.data && Array.isArray(initialFilterState.excludedTags2.data)) {
        initialFilterState.excludedTags2.data.forEach(tag => {
          // Find the matching tag in availableTags to get the correct id
          const matchingTag = availableTags.find(t => 
            (t.name && t.name.toLowerCase() === (tag.label || '').toLowerCase()) ||
            (t.label && t.label.toLowerCase() === (tag.label || '').toLowerCase()) ||
            t.id === tag.value
          );
          if (matchingTag) {
            const chipKey = getChipKey(matchingTag, 'tags2');
            newExcludedChips[chipKey] = true;
          }
        });
      }
      
      // 2. From tags2.excluded structure (from initial filter state)
      if (initialFilterState.tags2?.excluded && Array.isArray(initialFilterState.tags2.excluded)) {
        initialFilterState.tags2.excluded.forEach(tag => {
          // Find the matching tag in availableTags to get the correct id
          const matchingTag = availableTags.find(t => 
            (t.name && t.name.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
            (t.label && t.label.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
            t.id === (tag.id || tag.value)
          );
          if (matchingTag) {
            const chipKey = getChipKey(matchingTag, 'tags2');
            newExcludedChips[chipKey] = true;
          }
        });
      }
      
      // Process excluded genres
      if (initialFilterState.excludedGenres?.data && Array.isArray(initialFilterState.excludedGenres.data)) {
        initialFilterState.excludedGenres.data.forEach(genre => {
          const matchingGenre = availableGenres.find(g => 
            (g.name && g.name.toLowerCase() === (genre.label || '').toLowerCase()) ||
            (g.label && g.label.toLowerCase() === (genre.label || '').toLowerCase()) ||
            g.id === genre.value
          );
          if (matchingGenre) {
            const chipKey = getChipKey(matchingGenre, 'genres');
            newExcludedChips[chipKey] = true;
          }
        });
      }
      
      if (initialFilterState.genres?.excluded && Array.isArray(initialFilterState.genres.excluded)) {
        initialFilterState.genres.excluded.forEach(genre => {
          const matchingGenre = availableGenres.find(g => 
            (g.name && g.name.toLowerCase() === (genre.name || genre.label || '').toLowerCase()) ||
            (g.label && g.label.toLowerCase() === (genre.name || genre.label || '').toLowerCase()) ||
            g.id === (genre.id || genre.value)
          );
          if (matchingGenre) {
            const chipKey = getChipKey(matchingGenre, 'genres');
            newExcludedChips[chipKey] = true;
          }
        });
      }
      
      // Process other excluded filter types
      const excludedFilterConfigs = [
        { stateKey: 'excludedKeys', availableItems: availableKeys, section: 'keys' },
        { stateKey: 'excludedStudios', availableItems: availableStudios, section: 'studios' },
        { stateKey: 'excludedAgencies', availableItems: availableAgencies, section: 'agencies' },
        { stateKey: 'excludedClients', availableItems: availableClients, section: 'clients' },
        { stateKey: 'excludedProducers', availableItems: availableProducers, section: 'producers' },
        { stateKey: 'excludedComposers', availableItems: availableComposers, section: 'composers' },
        { stateKey: 'excludedCreativeDirectors', availableItems: availableCreativeDirectors, section: 'creativeDirectors' },
        { stateKey: 'excludedSessionArtists', availableItems: availableSessionArtists, section: 'sessionArtists' }
      ];
      
      excludedFilterConfigs.forEach(config => {
        if (initialFilterState[config.stateKey]?.data && Array.isArray(initialFilterState[config.stateKey].data)) {
          initialFilterState[config.stateKey].data.forEach(item => {
            const matchingItem = config.availableItems.find(availableItem => 
              (availableItem.name && availableItem.name.toLowerCase() === (item.label || '').toLowerCase()) ||
              (availableItem.label && availableItem.label.toLowerCase() === (item.label || '').toLowerCase()) ||
              availableItem.id === item.value
            );
            if (matchingItem) {
              const chipKey = getChipKey(matchingItem, config.section);
              newExcludedChips[chipKey] = true;
            }
          });
        }
        
        // Also check the alternative structure (e.g., initialFilterState.keys?.excluded)
        const alternativeKey = config.section;
        if (initialFilterState[alternativeKey]?.excluded && Array.isArray(initialFilterState[alternativeKey].excluded)) {
          initialFilterState[alternativeKey].excluded.forEach(item => {
            const matchingItem = config.availableItems.find(availableItem => 
              (availableItem.name && availableItem.name.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
              (availableItem.label && availableItem.label.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
              availableItem.id === (item.id || item.value)
            );
            if (matchingItem) {
              const chipKey = getChipKey(matchingItem, config.section);
              newExcludedChips[chipKey] = true;
            }
          });
        }
      });
      
      // Update excluded chips state in store with chip data
      // Clear all existing excluded chips first
      clearAllExcludedChips();
      // Then set the new excluded chips with their data
      Object.entries(newExcludedChips).forEach(([chipKey, isExcluded]) => {
        if (isExcluded) {
          // Find the actual chip data for this key
          const [section, ...idParts] = chipKey.split('_');
          const chipId = idParts.join('_');
          
          // Find the matching item from available items based on section
          let chipData = null;
          let availableItems = [];
          
          switch (section) {
            case 'tags':
              availableItems = availableTags;
              break;
            case 'tags2':
              availableItems = availableTags;
              break;
            case 'genres':
              availableItems = availableGenres;
              break;
            case 'keys':
              availableItems = availableKeys;
              break;
            case 'studios':
              availableItems = availableStudios;
              break;
            case 'agencies':
              availableItems = availableAgencies;
              break;
            case 'clients':
              availableItems = availableClients;
              break;
            case 'producers':
              availableItems = availableProducers;
              break;
            case 'composers':
              availableItems = availableComposers;
              break;
            case 'creativeDirectors':
              availableItems = availableCreativeDirectors;
              break;
            case 'sessionArtists':
              availableItems = availableSessionArtists;
              break;
            default:
              availableItems = [];
              break;
          }
          
          if (availableItems.length > 0) {
            chipData = availableItems.find(item => 
              item.id === chipId || `${section}_${item.id}` === chipKey
            );
          }
          
          setChipExcluded(chipKey, true, chipData);
        }
      });

      // Helper function to add excluded items to selected arrays
      const addExcludedItemsToSelected = (filterType, stateKey, excludedStateKey, availableItems, selectedItems, setSelectedItems) => {
        if (initialFilterState[filterType]?.selected) {
          // Process included items (existing logic)
          const mappedIncluded = initialFilterState[filterType].selected.map(item => {
            if (item.id && availableItems.some(av => av.id === item.id)) {
              return item;
            }
            const matchingItem = availableItems.find(av => 
              (av.name && av.name.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
              (av.label && av.label.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
              (av.rawData?.label && av.rawData.label.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
              (av.rawData?.name && av.rawData.name.toLowerCase() === (item.name || item.label || '').toLowerCase())
            );
            return matchingItem || item;
          }).filter(Boolean);

          // Also include excluded items from both structures
          const excludedFromState = [];
          
          // From filterState structure (e.g., excludedKeys.data)
          if (initialFilterState[excludedStateKey]?.data && Array.isArray(initialFilterState[excludedStateKey].data)) {
            initialFilterState[excludedStateKey].data.forEach(item => {
              const matchingItem = availableItems.find(av => 
                (av.name && av.name.toLowerCase() === (item.label || item.name || '').toLowerCase()) ||
                (av.label && av.label.toLowerCase() === (item.label || item.name || '').toLowerCase()) ||
                (av.rawData?.label && av.rawData.label.toLowerCase() === (item.label || item.name || '').toLowerCase()) ||
                (av.rawData?.name && av.rawData.name.toLowerCase() === (item.label || item.name || '').toLowerCase()) ||
                av.id === item.value
              );
              if (matchingItem) {
                excludedFromState.push(matchingItem);
              }
            });
          }
          
          // From initialFilterState structure (e.g., keys.excluded)
          if (initialFilterState[filterType]?.excluded && Array.isArray(initialFilterState[filterType].excluded)) {
            initialFilterState[filterType].excluded.forEach(item => {
              const matchingItem = availableItems.find(av => 
                (av.name && av.name.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
                (av.label && av.label.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
                (av.rawData?.label && av.rawData.label.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
                (av.rawData?.name && av.rawData.name.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
                av.id === (item.id || item.value)
              );
              if (matchingItem && !excludedFromState.some(existing => existing.id === matchingItem.id)) {
                excludedFromState.push(matchingItem);
              }
            });
          }

          // Combine included and excluded
          const allItems = [...mappedIncluded];
          excludedFromState.forEach(excludedItem => {
            if (!allItems.some(item => item.id === excludedItem.id)) {
              allItems.push(excludedItem);
            }
          });

          setSelectedItems(allItems);
        } else {
          // Handle case where there are only excluded items (no included items)
          const excludedFromState = [];
          
          if (initialFilterState[excludedStateKey]?.data && Array.isArray(initialFilterState[excludedStateKey].data)) {
            initialFilterState[excludedStateKey].data.forEach(item => {
              const matchingItem = availableItems.find(av => 
                (av.name && av.name.toLowerCase() === (item.label || '').toLowerCase()) ||
                (av.label && av.label.toLowerCase() === (item.label || '').toLowerCase()) ||
                av.id === item.value
              );
              if (matchingItem) {
                excludedFromState.push(matchingItem);
              }
            });
          }
          
          if (initialFilterState[filterType]?.excluded && Array.isArray(initialFilterState[filterType].excluded)) {
            initialFilterState[filterType].excluded.forEach(item => {
              const matchingItem = availableItems.find(av => 
                (av.name && av.name.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
                (av.label && av.label.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
                (av.rawData?.label && av.rawData.label.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
                (av.rawData?.name && av.rawData.name.toLowerCase() === (item.name || item.label || '').toLowerCase()) ||
                av.id === (item.id || item.value)
              );
              if (matchingItem && !excludedFromState.some(existing => existing.id === matchingItem.id)) {
                excludedFromState.push(matchingItem);
              }
            });
          }

          if (excludedFromState.length > 0) {
            setSelectedItems(excludedFromState);
          }
        }
      };

      // Update selected tags
      if (initialFilterState.tags?.selected) {
        // Map the incoming tags to actual Firebase options
        const mappedIncludedTags = initialFilterState.tags.selected.map(tag => {
          // If tag already has correct structure (from manual selection), use it
          if (tag.id && availableTags.some(t => t.id === tag.id)) {
            return tag;
          }
          // Otherwise, find the matching tag by name (case-insensitive)
          const matchingTag = availableTags.find(t => 
            (t.name && t.name.toLowerCase() === (tag.name || '').toLowerCase()) || 
            (t.label && t.label.toLowerCase() === (tag.name || '').toLowerCase())
          );
          return matchingTag || tag; // Return original if no match found
        }).filter(Boolean);
        
        // Also include any excluded chips from the new state to keep them in the UI
        const excludedTagsFromState = [];
        
        // Check both possible structures for excluded tags
        if (initialFilterState.excludedTags?.data && Array.isArray(initialFilterState.excludedTags.data)) {
          initialFilterState.excludedTags.data.forEach(tag => {
            const matchingTag = availableTags.find(t => 
              (t.name && t.name.toLowerCase() === (tag.label || '').toLowerCase()) ||
              (t.label && t.label.toLowerCase() === (tag.label || '').toLowerCase()) ||
              t.id === tag.value
            );
            if (matchingTag) {
              excludedTagsFromState.push(matchingTag);
            }
          });
        }
        
        if (initialFilterState.tags?.excluded && Array.isArray(initialFilterState.tags.excluded)) {
          initialFilterState.tags.excluded.forEach(tag => {
            const matchingTag = availableTags.find(t => 
              (t.name && t.name.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
              (t.label && t.label.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
              t.id === (tag.id || tag.value)
            );
            if (matchingTag && !excludedTagsFromState.some(existing => existing.id === matchingTag.id)) {
              excludedTagsFromState.push(matchingTag);
            }
          });
        }
        
        // Combine included and excluded tags, removing duplicates
        const allTags = [...mappedIncludedTags];
        excludedTagsFromState.forEach(excludedTag => {
          if (!allTags.some(tag => tag.id === excludedTag.id)) {
            allTags.push(excludedTag);
          }
        });
        
        
        setSelectedTags(allTags);
        
        // Also update logic type here since tags are selected
        if (initialFilterState.tags.logic) {
          setTagLogicType(initialFilterState.tags.logic.toLowerCase());
        }
      } else {
        // Handle case where there are only excluded tags (no included tags)
        const excludedTagsFromState = [];
        
        // Check both possible structures for excluded tags
        if (initialFilterState.excludedTags?.data && Array.isArray(initialFilterState.excludedTags.data)) {
          initialFilterState.excludedTags.data.forEach(tag => {
            const matchingTag = availableTags.find(t => 
              (t.name && t.name.toLowerCase() === (tag.label || '').toLowerCase()) ||
              (t.label && t.label.toLowerCase() === (tag.label || '').toLowerCase()) ||
              t.id === tag.value
            );
            if (matchingTag) {
              excludedTagsFromState.push(matchingTag);
            }
          });
        }
        
        if (initialFilterState.tags?.excluded && Array.isArray(initialFilterState.tags.excluded)) {
          initialFilterState.tags.excluded.forEach(tag => {
            const matchingTag = availableTags.find(t => 
              (t.name && t.name.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
              (t.label && t.label.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
              t.id === (tag.id || tag.value)
            );
            if (matchingTag && !excludedTagsFromState.some(existing => existing.id === matchingTag.id)) {
              excludedTagsFromState.push(matchingTag);
            }
          });
        }
        
        if (excludedTagsFromState.length > 0) {
          setSelectedTags(excludedTagsFromState);
        }
      }
      
      // Update tag logic type (independent of selected tags) - fallback
      if (initialFilterState.tags?.logic && !initialFilterState.tags?.selected) {
        setTagLogicType(initialFilterState.tags.logic.toLowerCase());
      }

      // Update selected tags2
      if (initialFilterState.tags2?.selected) {
        // Map the incoming tags2 to actual Firebase options
        const mappedIncludedTags2 = initialFilterState.tags2.selected.map(tag => {
          // If tag already has correct structure (from manual selection), use it
          if (tag.id && availableTags.some(t => t.id === tag.id)) {
            return tag;
          }
          // Otherwise, find the matching tag by name (case-insensitive)
          const matchingTag = availableTags.find(t => 
            (t.name && t.name.toLowerCase() === (tag.name || '').toLowerCase()) || 
            (t.label && t.label.toLowerCase() === (tag.name || '').toLowerCase())
          );
          return matchingTag || tag; // Return original if no match found
        }).filter(Boolean);
        
        // Also include any excluded tags2 from the new state to keep them in the UI
        const excludedTags2FromState = [];
        
        // Check both possible structures for excluded tags2
        if (initialFilterState.excludedTags2?.data && Array.isArray(initialFilterState.excludedTags2.data)) {
          initialFilterState.excludedTags2.data.forEach(tag => {
            const matchingTag = availableTags.find(t => 
              (t.name && t.name.toLowerCase() === (tag.label || '').toLowerCase()) ||
              (t.label && t.label.toLowerCase() === (tag.label || '').toLowerCase()) ||
              t.id === tag.value
            );
            if (matchingTag) {
              excludedTags2FromState.push(matchingTag);
            }
          });
        }
        
        if (initialFilterState.tags2?.excluded && Array.isArray(initialFilterState.tags2.excluded)) {
          initialFilterState.tags2.excluded.forEach(tag => {
            const matchingTag = availableTags.find(t => 
              (t.name && t.name.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
              (t.label && t.label.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
              t.id === (tag.id || tag.value)
            );
            if (matchingTag && !excludedTags2FromState.some(existing => existing.id === matchingTag.id)) {
              excludedTags2FromState.push(matchingTag);
            }
          });
        }
        
        // Combine included and excluded tags2, removing duplicates
        const allTags2 = [...mappedIncludedTags2];
        excludedTags2FromState.forEach(excludedTag => {
          if (!allTags2.some(tag => tag.id === excludedTag.id)) {
            allTags2.push(excludedTag);
          }
        });
        
        setSelectedTags2(allTags2);
        
        // Also update logic type here since tags2 are selected
        if (initialFilterState.tags2.logic) {
          setTagLogicType2(initialFilterState.tags2.logic.toLowerCase());
        }
      } else {
        // Handle case where there are only excluded tags2 (no included tags2)
        const excludedTags2FromState = [];
        
        // Check both possible structures for excluded tags2
        if (initialFilterState.excludedTags2?.data && Array.isArray(initialFilterState.excludedTags2.data)) {
          initialFilterState.excludedTags2.data.forEach(tag => {
            const matchingTag = availableTags.find(t => 
              (t.name && t.name.toLowerCase() === (tag.label || '').toLowerCase()) ||
              (t.label && t.label.toLowerCase() === (tag.label || '').toLowerCase()) ||
              t.id === tag.value
            );
            if (matchingTag) {
              excludedTags2FromState.push(matchingTag);
            }
          });
        }
        
        if (initialFilterState.tags2?.excluded && Array.isArray(initialFilterState.tags2.excluded)) {
          initialFilterState.tags2.excluded.forEach(tag => {
            const matchingTag = availableTags.find(t => 
              (t.name && t.name.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
              (t.label && t.label.toLowerCase() === (tag.name || tag.label || '').toLowerCase()) ||
              t.id === (tag.id || tag.value)
            );
            if (matchingTag && !excludedTags2FromState.some(existing => existing.id === matchingTag.id)) {
              excludedTags2FromState.push(matchingTag);
            }
          });
        }
        
        if (excludedTags2FromState.length > 0) {
          setSelectedTags2(excludedTags2FromState);
        }
      }
      
      // Update tag2 logic type (independent of selected tags2) - fallback
      if (initialFilterState.tags2?.logic && !initialFilterState.tags2?.selected) {
        setTagLogicType2(initialFilterState.tags2.logic.toLowerCase());
      }

      // Update selected genres
      if (initialFilterState.genres?.selected) {
        // Map the incoming genres to actual Firebase options
        const mappedGenres = initialFilterState.genres.selected.map(genre => {
          // If genre already has correct structure (from manual selection), use it
          if (genre.id && availableGenres.some(g => g.id === genre.id)) {
            return genre;
          }
          // Otherwise, find the matching genre by name
          const matchingGenre = availableGenres.find(g => g.name === genre.name || g.label === genre.name);
          return matchingGenre || genre; // Return original if no match found
        }).filter(Boolean);
        
        // Also include any excluded genres from the new state to keep them in the UI
        const excludedGenresFromState = [];
        
        // Check both possible structures for excluded genres
        if (initialFilterState.excludedGenres?.data && Array.isArray(initialFilterState.excludedGenres.data)) {
          initialFilterState.excludedGenres.data.forEach(genre => {
            const matchingGenre = availableGenres.find(g => 
              (g.name && g.name.toLowerCase() === (genre.label || '').toLowerCase()) ||
              (g.label && g.label.toLowerCase() === (genre.label || '').toLowerCase()) ||
              g.id === genre.value
            );
            if (matchingGenre) {
              excludedGenresFromState.push(matchingGenre);
            }
          });
        }
        
        if (initialFilterState.genres?.excluded && Array.isArray(initialFilterState.genres.excluded)) {
          initialFilterState.genres.excluded.forEach(genre => {
            const matchingGenre = availableGenres.find(g => 
              (g.name && g.name.toLowerCase() === (genre.name || genre.label || '').toLowerCase()) ||
              (g.label && g.label.toLowerCase() === (genre.name || genre.label || '').toLowerCase()) ||
              g.id === (genre.id || genre.value)
            );
            if (matchingGenre && !excludedGenresFromState.some(existing => existing.id === matchingGenre.id)) {
              excludedGenresFromState.push(matchingGenre);
            }
          });
        }
        
        // Combine included and excluded genres, removing duplicates
        const allGenres = [...mappedGenres];
        excludedGenresFromState.forEach(excludedGenre => {
          if (!allGenres.some(genre => genre.id === excludedGenre.id)) {
            allGenres.push(excludedGenre);
          }
        });
        
        setSelectedGenres(allGenres);
      } else {
        // Handle case where there are only excluded genres (no included genres)
        const excludedGenresFromState = [];
        
        // Check both possible structures for excluded genres
        if (initialFilterState.excludedGenres?.data && Array.isArray(initialFilterState.excludedGenres.data)) {
          initialFilterState.excludedGenres.data.forEach(genre => {
            const matchingGenre = availableGenres.find(g => 
              (g.name && g.name.toLowerCase() === (genre.label || '').toLowerCase()) ||
              (g.label && g.label.toLowerCase() === (genre.label || '').toLowerCase()) ||
              g.id === genre.value
            );
            if (matchingGenre) {
              excludedGenresFromState.push(matchingGenre);
            }
          });
        }
        
        if (initialFilterState.genres?.excluded && Array.isArray(initialFilterState.genres.excluded)) {
          initialFilterState.genres.excluded.forEach(genre => {
            const matchingGenre = availableGenres.find(g => 
              (g.name && g.name.toLowerCase() === (genre.name || genre.label || '').toLowerCase()) ||
              (g.label && g.label.toLowerCase() === (genre.name || genre.label || '').toLowerCase()) ||
              g.id === (genre.id || genre.value)
            );
            if (matchingGenre && !excludedGenresFromState.some(existing => existing.id === matchingGenre.id)) {
              excludedGenresFromState.push(matchingGenre);
            }
          });
        }
        
        if (excludedGenresFromState.length > 0) {
          setSelectedGenres(excludedGenresFromState);
        }
      }
      
      // Update genre logic type (independent of selected genres)
      if (initialFilterState.genres?.logic) {
        setGenreLogicType(initialFilterState.genres.logic.toLowerCase());
      }

      // Update BPM filter, or reset if not present
      if (initialFilterState.bpm) {
        setBpmFilterType(initialFilterState.bpm.filterType || 'exact');
        setSelectedBPM(initialFilterState.bpm.exact || null);
        setBpmRange(initialFilterState.bpm.range || { min: '', max: '' });
      } else {
        setBpmFilterType('exact');
        setSelectedBPM(null);
        setBpmRange({ min: '', max: '' });
      }

      // Update creative directors
      if (initialFilterState.creativeDirectors?.selected) {
        // Map the incoming creative directors to actual Firebase options
        const mappedCreativeDirectors = initialFilterState.creativeDirectors.selected.map(cd => {
          // If creative director already has correct structure (from manual selection), use it
          if (cd.id && availableCreativeDirectors.some(c => c.id === cd.id)) {
            return cd;
          }
          // Otherwise, find the matching creative director by name
          const matchingCD = availableCreativeDirectors.find(c => c.name === cd.name);
          return matchingCD || cd; // Return original if no match found
        }).filter(Boolean);
        
        setSelectedCreativeDirectors(mappedCreativeDirectors);
        setCreativeDirectorLogicType(
          initialFilterState.creativeDirectors.logic
            ? initialFilterState.creativeDirectors.logic.toLowerCase()
            : LOGIC_TYPES.OR
        );
      }

      // Update session artists
      if (initialFilterState.sessionArtists?.selected) {
        
        // Map the incoming session artists to actual Firebase options
        const mappedSessionArtists = initialFilterState.sessionArtists.selected.map(sa => {
          // If session artist already has correct structure (from manual selection), use it
          if (sa.id && availableSessionArtists.some(s => s.id === sa.id)) {
            return sa;
          }
          // Otherwise, find the matching session artist by name
          const matchingSA = availableSessionArtists.find(s => s.name === sa.name);
          return matchingSA || sa; // Return original if no match found
        }).filter(Boolean);
        
        
        setSelectedSessionArtists(mappedSessionArtists);
        setSessionArtistLogicType(
          initialFilterState.sessionArtists.logic
            ? initialFilterState.sessionArtists.logic.toLowerCase()
            : LOGIC_TYPES.OR
        );
      }

      // Update all filter types using helper function for excluded support
      addExcludedItemsToSelected('keys', 'keys', 'excludedKeys', availableKeys, selectedKeys, setSelectedKeys);
      addExcludedItemsToSelected('studios', 'studios', 'excludedStudios', availableStudios, selectedStudios, setSelectedStudios);
      addExcludedItemsToSelected('producers', 'producers', 'excludedProducers', availableProducers, selectedProducers, setSelectedProducers);
      addExcludedItemsToSelected('composers', 'composers', 'excludedComposers', availableComposers, selectedComposers, setSelectedComposers);
      addExcludedItemsToSelected('agencies', 'agencies', 'excludedAgencies', availableAgencies, selectedAgencies, setSelectedAgencies);
      addExcludedItemsToSelected('clients', 'clients', 'excludedClients', availableClients, selectedClients, setSelectedClients);
      addExcludedItemsToSelected('creativeDirectors', 'creativeDirectors', 'excludedCreativeDirectors', availableCreativeDirectors, selectedCreativeDirectors, setSelectedCreativeDirectors);
      addExcludedItemsToSelected('sessionArtists', 'sessionArtists', 'excludedSessionArtists', availableSessionArtists, selectedSessionArtists, setSelectedSessionArtists);
      
      // Update logic types
      if (initialFilterState.keys?.logic) {
        setKeyLogicType(initialFilterState.keys.logic.toLowerCase());
      }
      if (initialFilterState.studios?.logic) {
        setStudioLogicType(initialFilterState.studios.logic.toLowerCase());
      }
      if (initialFilterState.producers?.logic) {
        setProducerLogicType(initialFilterState.producers.logic.toLowerCase());
      }
      if (initialFilterState.composers?.logic) {
        setComposerLogicType(initialFilterState.composers.logic.toLowerCase());
      }
      if (initialFilterState.agencies?.logic) {
        setAgencyLogicType(initialFilterState.agencies.logic.toLowerCase());
      }
      if (initialFilterState.clients?.logic) {
        setClientLogicType(initialFilterState.clients.logic.toLowerCase());
      }
      if (initialFilterState.creativeDirectors?.logic) {
        setCreativeDirectorLogicType(initialFilterState.creativeDirectors.logic.toLowerCase());
      }
      if (initialFilterState.sessionArtists?.logic) {
        setSessionArtistLogicType(initialFilterState.sessionArtists.logic.toLowerCase());
      }

      // Update date filter
      if (initialFilterState.date?.range) {
        setDateFilterType(initialFilterState.date.filterType || 'completed');
        setDateRange(initialFilterState.date.range);
      }

      // Update track name search
      if (initialFilterState.text) {
        setTrackNameSearch(initialFilterState.text.value || '');
      }

      // Update license filter, or reset if not present
      if (initialFilterState.filters?.license?.value !== undefined) {
        setLicenseFilter(initialFilterState.filters.license.value ? 'licensed' : 'unlicensed');
      } else if (initialFilterState.license?.value !== undefined) {
        setLicenseFilter(initialFilterState.license.value ? 'licensed' : 'unlicensed');
      } else {
        setLicenseFilter('all');
      }

      // Reset the flag after a short delay to allow state updates to complete
      timeoutId = setTimeout(() => {
        setIsInitialFilterStateUpdate(false);
      }, 0);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilterState, availableTags, availableGenres, availableKeys, availableStudios, availableAgencies, availableClients, availableProducers, availableComposers, availableCreativeDirectors, availableSessionArtists]); // Re-run when initialFilterState changes or when available data loads

  // Track if we're in the middle of a chip toggle to prevent immediate filter emission
  const [isTogglingChip, setIsTogglingChip] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const [isEnterKeySelection, setIsEnterKeySelection] = useState(false);

  // Debug: Track selectedTags changes
  useEffect(() => {
  }, [selectedTags]);

  // Debug: Track initialFilterState changes
  useEffect(() => {
    if (initialFilterState) {
    }
  }, [initialFilterState]);

  // Update the useEffect that emits filter changes
  useEffect(() => {
    if (!onFiltersChange || isInitialFilterStateUpdate || isTogglingChip) {
      return;
    }

    let mounted = true;

    // Helper function to split included/excluded items
    const splitIncludedExcluded = (items, section) => {
      const included = [];
      const excluded = [];
      
      items.forEach(item => {
        const chipKey = getChipKey(item, section);
        if (excludedChips[chipKey]) {
          excluded.push(item);
        } else {
          included.push(item);
        }
      });
      
      return { included, excluded };
    };

    const tagsData = splitIncludedExcluded(selectedTags, 'tags');
    const tags2Data = splitIncludedExcluded(selectedTags2, 'tags2');
    const genresData = splitIncludedExcluded(selectedGenres, 'genres');
    const keysData = splitIncludedExcluded(selectedKeys, 'keys');
    const creativeDirectorsData = splitIncludedExcluded(selectedCreativeDirectors, 'creativeDirectors');
    const sessionArtistsData = splitIncludedExcluded(selectedSessionArtists, 'sessionArtists');
    const agenciesData = splitIncludedExcluded(selectedAgencies, 'agencies');
    const clientsData = splitIncludedExcluded(selectedClients, 'clients');
    const studiosData = splitIncludedExcluded(selectedStudios, 'studios');
    const producersData = splitIncludedExcluded(selectedProducers, 'producers');
    const composersData = splitIncludedExcluded(selectedComposers, 'composers');

    const filters = {
      tags: {
        selected: tagsData.included,
        excluded: tagsData.excluded,
        logic: tagLogicType
      },
      tags2: {
        selected: tags2Data.included,
        excluded: tags2Data.excluded,
        logic: tagLogicType2
      },
      creativeDirectors: {
        selected: creativeDirectorsData.included,
        excluded: creativeDirectorsData.excluded,
        logic: creativeDirectorLogicType
      },
      sessionArtists: {
        selected: sessionArtistsData.included,
        excluded: sessionArtistsData.excluded,
        logic: sessionArtistLogicType
      },
      genres: {
        selected: genresData.included,
        excluded: genresData.excluded,
        logic: genreLogicType
      },
      agencies: {
        selected: agenciesData.included,
        excluded: agenciesData.excluded,
        logic: agencyLogicType
      },
      clients: {
        selected: clientsData.included,
        excluded: clientsData.excluded,
        logic: clientLogicType
      },
      keys: {
        selected: keysData.included,
        excluded: keysData.excluded,
        logic: keyLogicType
      },
      studios: {
        selected: studiosData.included,
        excluded: studiosData.excluded,
        logic: studioLogicType
      },
      producers: {
        selected: producersData.included,
        excluded: producersData.excluded,
        logic: producerLogicType
      },
      composers: {
        selected: composersData.included,
        excluded: composersData.excluded,
        logic: composerLogicType
      },
      bpm: {
        filterType: bpmFilterType,
        exact: selectedBPM,
        range: bpmRange
      },
      date: {
        filterType: dateFilterType,
        range: dateRange
      },
      text: trackNameSearch ? {
        value: trackNameSearch,
        field: 'trackName'
      } : null,
      license: licenseFilter !== 'all' ? {
        value: licenseFilter === 'licensed'
      } : null,
      expandedSections: expandedSections
    };

    // Only emit if filters have changed
    const filtersJson = JSON.stringify(filters);
    if (prevFiltersRef.current !== filtersJson) {
      // Store the current expanded sections state before emitting
      const currentExpandedSections = { ...expandedSections };
      
      
      prevFiltersRef.current = filtersJson;
      onFiltersChange(filters);
      
      // Ensure the expanded sections state is preserved after emitting
      if (mounted) {
        setTimeout(() => {
          if (mounted && JSON.stringify(currentExpandedSections) !== JSON.stringify(expandedSections)) {
            setExpandedSections(currentExpandedSections);
          }
        }, 0);
      }
    }

    return () => {
      mounted = false;
    };
  }, [
    selectedTags,
    selectedTags2,
    selectedCreativeDirectors,
    selectedSessionArtists,
    selectedClients,
    selectedKeys,
    selectedStudios,
    selectedProducers,
    tagLogicType,
    tagLogicType2,
    creativeDirectorLogicType,
    sessionArtistLogicType,
    clientLogicType,
    keyLogicType,
    studioLogicType,
    producerLogicType,
    selectedComposers,
    composerLogicType,
    selectedBPM,
    bpmFilterType,
    bpmRange,
    selectedGenres,
    genreLogicType,
    trackNameSearch,
    dateFilterType,
    dateRange,
    licenseFilter,
    expandedSections,
    onFiltersChange,
    isInitialFilterStateUpdate,
    agencyLogicType,
    selectedAgencies,
    excludedChips,
    getChipKey,
    isTogglingChip
  ]);

  const handleBPMChange = (event, newValue) => {
    setSelectedBPM(newValue);
  };

  const handleBpmFilterTypeChange = (event) => {
    setBpmFilterType(event.target.value);
    // Clear the other type of filter when switching
    if (event.target.value === 'exact') {
      setBpmRange({ min: '', max: '' });
    } else {
      setSelectedBPM(null);
    }
  };

  const handleBpmRangeChange = (type) => (event) => {
    const value = event.target.value;
    
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setBpmRange(prev => ({
        ...prev,
        [type]: value
      }));
    }
  };

  const handleDateFilterTypeChange = (event) => {
    setDateFilterType(event.target.value);
  };

  const handleDateRangeChange = (type) => (event) => {
    // Persist the event since we're using it in an async context
    event.persist();
    
    const value = event.target.value;
    if (!value) {
      setDateRange(prev => ({
        ...prev,
        [type]: null
      }));
      return;
    }

    // Create a date object from the input value
    const [year, month, day] = value.split('-').map(Number);
    
    // Create a new date object with the exact date components in UTC
    const date = new Date(Date.UTC(year, month - 1, day));
    
    if (isNaN(date.getTime())) {
      return;
    }

    // Set the time to start or end of day in UTC
    if (type === 'from') {
      date.setUTCHours(0, 0, 0, 0);
    } else {
      date.setUTCHours(23, 59, 59, 999);
    }
    
    const timestamp = date.getTime();
    
    setDateRange(prev => ({
      ...prev,
      [type]: timestamp
    }));
  };

  const handleSectionExpand = (section) => (expanded) => {
    manuallyExpandedSectionsRef.current[section] = true;
    
    setExpandedSections(prev => {
      const newState = {
        ...prev,
        [section]: expanded
      };
      return newState;
    });
  };

  const clearAllFilters = () => {
    // Clear all selected values
    setSelectedTags([]);
    setSelectedTags2([]);
    setSelectedCreativeDirectors([]);
    setSelectedSessionArtists([]);
    setSelectedGenres([]);
    setSelectedAgencies([]);
    setSelectedClients([]);
    setSelectedKeys([]);
    setSelectedStudios([]);
    setSelectedProducers([]);
    setSelectedComposers([]);
    setSelectedBPM(null);
    setBpmRange({ min: '', max: '' });
    setDateRange({ from: null, to: null });
    setTrackNameSearch('');
    setLicenseFilter('all');

    // Clear excluded chips state from store
    clearAllExcludedChips();

    // Reset all logic types to OR
    setTagLogicType(LOGIC_TYPES.OR);
    setTagLogicType2(LOGIC_TYPES.OR);
    setCreativeDirectorLogicType(LOGIC_TYPES.OR);
    setSessionArtistLogicType(LOGIC_TYPES.OR);
    setGenreLogicType(LOGIC_TYPES.OR);
    setAgencyLogicType(LOGIC_TYPES.OR);
    setClientLogicType(LOGIC_TYPES.OR);
    setKeyLogicType(LOGIC_TYPES.OR);
    setStudioLogicType(LOGIC_TYPES.OR);
    setProducerLogicType(LOGIC_TYPES.OR);
    setComposerLogicType(LOGIC_TYPES.OR);

    // Reset BPM filter type
    setBpmFilterType('exact');

    // Collapse all sections
    setExpandedSections({
      trackName: false,
      license: false,
      tags: false,
      tags2: false,
      composers: false,
      creativeDirectors: false,
      sessionArtists: false,
      genres: false,
      agencies: false,
      clients: false,
      keys: false,
      studios: false,
      producers: false,
      bpm: false,
      date: false
    });

    // Add filter reset flag to chat history
    addFilterResetFlag();

    // Reset mnemonic and licensed overrides in parent component
    if (onMnemonicReset) {
      onMnemonicReset();
    }
    if (onLicensedReset) {
      onLicensedReset();
    }

    // Create empty filters object
    const emptyFilters = {
      tags: { selected: [], logic: LOGIC_TYPES.OR },
      tags2: { selected: [], logic: LOGIC_TYPES.OR },
      creativeDirectors: { selected: [], logic: LOGIC_TYPES.OR },
      sessionArtists: { selected: [], logic: LOGIC_TYPES.OR },
      genres: { selected: [], logic: LOGIC_TYPES.OR },
      agencies: { selected: [], logic: LOGIC_TYPES.OR },
      clients: { selected: [], logic: LOGIC_TYPES.OR },
      keys: { selected: [], logic: LOGIC_TYPES.OR },
      studios: { selected: [], logic: LOGIC_TYPES.OR },
      producers: { selected: [], logic: LOGIC_TYPES.OR },
      composers: { selected: [], logic: LOGIC_TYPES.OR },
      bpm: { filterType: 'exact', exact: null, range: { min: '', max: '' } },
      date: { filterType: 'completed', range: { from: null, to: null } },
      text: null,
      license: null
    };

    // Update the prevFiltersRef with empty filters
    prevFiltersRef.current = JSON.stringify(emptyFilters);

    // Force a filter change event with empty filters
    if (onFiltersChange) {
      onFiltersChange(emptyFilters);
    }
  };

  // Add test button handlers
  const handleTestTagFilter = () => {
    setSelectedTags([availableTags[0]]);
    setTagLogicType(LOGIC_TYPES.OR);
    
    // Expand the tags section
    setExpandedSections(prev => ({
      ...prev,
      tags: true
    }));
    manuallyExpandedSectionsRef.current.tags = true;
    
    if (onFiltersChange) {
      onFiltersChange({
        ...prevFiltersRef.current,
        tags: { value: [availableTags[0]], logic: LOGIC_TYPES.OR }
      });
    }
  };

  const handleTestGenreFilter = () => {
    setSelectedGenres([availableGenres[0]]);
    setGenreLogicType(LOGIC_TYPES.OR);
    
    // Expand the genres section
    setExpandedSections(prev => ({
      ...prev,
      genres: true
    }));
    manuallyExpandedSectionsRef.current.genres = true;
  };

  const handleTestBPMFilter = () => {
    setSelectedBPM({ id: '120', label: '120', value: 120 });
    setBpmFilterType('exact');
    
    // Expand the BPM section
    setExpandedSections(prev => ({
      ...prev,
      bpm: true
    }));
    manuallyExpandedSectionsRef.current.bpm = true;
  };

  const handleTestCreativeDirectorFilter = () => {
    setSelectedCreativeDirectors([{ id: 'cd1', name: 'Shane Forbes' }]);
    setCreativeDirectorLogicType(LOGIC_TYPES.OR);
    
    // Expand the creative directors section
    setExpandedSections(prev => ({
      ...prev,
      creativeDirectors: true
    }));
    manuallyExpandedSectionsRef.current.creativeDirectors = true;
  };

  // eslint-disable-next-line no-unused-vars
  const handleTestSessionArtistFilter = () => {
    setSelectedSessionArtists([{ id: 'sa1', name: 'Simz' }]);
    setSessionArtistLogicType(LOGIC_TYPES.OR);
    
    // Expand the session artists section
    setExpandedSections(prev => ({
      ...prev,
      sessionArtists: true
    }));
    manuallyExpandedSectionsRef.current.sessionArtists = true;
  };

  // eslint-disable-next-line no-unused-vars
  const handleTestAgencyFilter = () => {
    setSelectedAgencies([{ id: 'agency1', name: '99 Cents' }]);
    setAgencyLogicType(LOGIC_TYPES.OR);
    
    // Expand the agencies section
    setExpandedSections(prev => ({
      ...prev,
      agencies: true
    }));
    manuallyExpandedSectionsRef.current.agencies = true;
  };

  // eslint-disable-next-line no-unused-vars
  const handleTestClientFilter = () => {
    setSelectedClients([{ id: 'client1', name: 'KFC' }]);
    setClientLogicType(LOGIC_TYPES.OR);
    
    // Expand the clients section
    setExpandedSections(prev => ({
      ...prev,
      clients: true
    }));
    manuallyExpandedSectionsRef.current.clients = true;
  };

  // eslint-disable-next-line no-unused-vars
  const handleTestKeyFilter = () => {
    // Find the A minor key from available keys
    const aMinorKey = availableKeys.find(key => 
      key.name.toLowerCase() === 'a minor' || 
      key.rawData?.label?.toLowerCase() === 'a minor' ||
      key.rawData?.name?.toLowerCase() === 'a minor'
    );
    
    if (aMinorKey) {
      setSelectedKeys([aMinorKey]);
      setKeyLogicType(LOGIC_TYPES.OR);
      
      // Expand the keys section
      setExpandedSections(prev => ({
        ...prev,
        keys: true
      }));
      manuallyExpandedSectionsRef.current.keys = true;
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleTestStudioFilter = () => {
    // Find the A2 studio from available studios
    const a2Studio = availableStudios.find(studio => 
      studio.name.toLowerCase() === 'a2' || 
      studio.rawData?.label?.toLowerCase() === 'a2' ||
      studio.rawData?.name?.toLowerCase() === 'a2'
    );
    
    if (a2Studio) {
      setSelectedStudios([a2Studio]);
      setStudioLogicType(LOGIC_TYPES.OR);
      
      // Expand the studios section
      setExpandedSections(prev => ({
        ...prev,
        studios: true
      }));
      manuallyExpandedSectionsRef.current.studios = true;
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleTestProducerFilter = () => {
    // Find Nicola Berry from available producers
    const nicolaBerry = availableProducers.find(producer => 
      producer.name.toLowerCase() === 'nicola berry' || 
      producer.rawData?.label?.toLowerCase() === 'nicola berry' ||
      producer.rawData?.name?.toLowerCase() === 'nicola berry'
    );
    
    if (nicolaBerry) {
      setSelectedProducers([nicolaBerry]);
      setProducerLogicType(LOGIC_TYPES.OR);
      
      // Expand the producers section
      setExpandedSections(prev => ({
        ...prev,
        producers: true
      }));
      manuallyExpandedSectionsRef.current.producers = true;
    }
  };

  const handleTestComposerFilter = () => {
    setSelectedComposers([{ id: 'comp1', name: 'Paul Norwood' }]);
    setComposerLogicType(LOGIC_TYPES.OR);
    
    // Expand the composers section
    setExpandedSections(prev => ({
      ...prev,
      composers: true
    }));
    manuallyExpandedSectionsRef.current.composers = true;
  };


  const handleTestDateFilter = () => {
    // Set date filter to search from 2024/01/01 to 2025/01/01
    const startDate = new Date('2024-01-01');
    const endDate = new Date('2025-01-01');
    
    setDateFilterType('completed');
    setDateRange({
      from: startDate.getTime(),
      to: endDate.getTime()
    });
    
    // Expand the date section
    setExpandedSections(prev => ({
      ...prev,
      date: true
    }));
    manuallyExpandedSectionsRef.current.date = true;
  };

  const handleTestLicenseFilter = () => {
    setLicenseFilter('licensed');
    
    // Expand the license section
    setExpandedSections(prev => ({
      ...prev,
      license: true
    }));
    manuallyExpandedSectionsRef.current.license = true;
    
    if (onFiltersChange) {
      onFiltersChange({
        ...prevFiltersRef.current,
        license: { value: true }
      });
    }
  };

  // Update the Autocomplete components to include keyboard navigation
  const commonAutocompleteProps = (section, options, selectedOptions, setSelectedOptions) => ({
    multiple: true,
    options: options,
    getOptionLabel: (option) => option.name,
    value: selectedOptions,
    disableCloseOnSelect: !isEnterKeySelection,
    onChange: (event, newValue, reason) => {
      // Don't update selections if we're just toggling chip state
      if (!isTogglingChip) {
        setSelectedOptions(newValue);
      }
      
      // Reset enter key flag after change
      if (isEnterKeySelection) {
        setTimeout(() => setIsEnterKeySelection(false), 100);
      }
    },
    onKeyDown: (event) => {
      // Track if Enter key was pressed for selection
      if (event.key === 'Enter') {
        setIsEnterKeySelection(true);
      }
      handleDropdownKeyDown(event);
    },
    getOptionSelected: (option, value) => {
      // Compare by name to handle cases where chatbot sets filters with label as ID
      return option.name === value.name || option.id === value.id;
    },
    renderInput: (params) => (
      <TextField
        {...params}
        variant="outlined"
        placeholder={`Select ${section}`}
        className={classes.autocomplete}
      />
    ),
    ListboxProps: {
      style: {
        fontSize: 'var(--menu-font-size)'
      }
    },
    renderTags: (value, getTagProps) =>
      value.map((option, index) => {
        const chipKey = getChipKey(option, section);
        const isExcluded = excludedChips[chipKey];
        const tagProps = getTagProps({ index });
        
        // Only keep the size and variant props, remove event handlers
        const { onDelete, onClick, onKeyDown, onMouseDown, ...safeTagProps } = tagProps;
        
        
        return (
          <Chip
            key={option.id || option.name}
            label={option.name}
            size={safeTagProps.size}
            variant={safeTagProps.variant}
            className={`custom-chip ${isExcluded ? 'excluded-chip' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleChipToggle(option, section);
            }}
            onDelete={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleChipRemove(option, section, selectedOptions, setSelectedOptions);
            }}
            title={isExcluded ? 'Click to include this item in search' : 'Click to exclude this item from search'}
            style={{
              cursor: 'pointer'
            }}
          />
        );
      }),
    renderOption: (option, { selected, focused }) => {
      const optionKey = `${section}_${option.id || option.name}`;
      const isHovered = hoveredOption === optionKey;
      
      return (
        <div 
          style={{ 
            backgroundColor: selected
              ? 'var(--accent-1-color)'
              : focused
                ? 'rgba(255, 0, 0, 0.5)'
                : 'transparent',
            color: '#fff',
            cursor: 'pointer',
            borderRadius: selected ? '8px' : undefined,
            padding: selected ? '4px 16px' : '4px 16px',
            margin: selected ? '2px 0' : '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minWidth: '80%',
            transition: 'background 0.15s, color 0.15s',
            position: 'relative'
          }}
          onMouseEnter={() => setHoveredOption(optionKey)}
          onMouseLeave={() => setHoveredOption(null)}
        >
          <span>{option.name}</span>
          {isHovered && (
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', height: '16px' }}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Add the item as included (same as clicking on the item text)
                  if (!selectedOptions.some(item => item.id === option.id)) {
                    setSelectedOptions(prev => [...prev, option]);
                  }
                }}
                style={{ 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  fontSize: '18px', 
                  fontWeight: 'bold',
                  marginRight: '4px',
                  lineHeight: '1',
                  transition: 'color 0.15s ease',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: '0 4px',
                  minWidth: '16px',
                  height: '16px'
                }}
                onMouseEnter={(e) => e.target.style.color = 'rgba(255, 255, 255, 1)'}
                onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.5)'}
                title="Add as included item"
              >
                +
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDirectExclude(option, section, selectedOptions, setSelectedOptions);
                }}
                style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: '0 4px',
                  cursor: 'pointer',
                  minWidth: '16px',
                  height: '16px',
                  lineHeight: '1',
                  transition: 'color 0.15s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = 'rgba(255, 255, 255, 1)'}
                onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.5)'}
                title="Add as excluded item"
              >
                -
              </button>
            </div>
          )}
        </div>
      );
    },
    'data-section': section
  });

  // Update the filter renderers to use the common props
  const filterRenderers = {
    trackName: () => (
      <FilterSection
        key="trackName"
        icon="fas fa-search"
        title="Track Name"
        selectedCount={trackNameSearch ? 1 : 0}
        isExpanded={expandedSections.trackName}
        onExpandChange={handleSectionExpand('trackName')}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by track name..."
          value={trackNameSearch}
          onChange={(e) => setTrackNameSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === ' ') {
              e.stopPropagation();
            }
          }}
          className={classes.trackNameInput}
        />
      </FilterSection>
    ),
    tags: () => (
      <FilterSection
        key="tags"
        icon="fas fa-tags"
        title="Tags 1"
        selectedCount={selectedTags.length}
        isExpanded={expandedSections.tags}
        onExpandChange={handleSectionExpand('tags')}
      >
        <CategorizedAutocomplete
          {...commonAutocompleteProps('tags', availableTags, selectedTags, setSelectedTags)}
          autoHighlight
          section="tags"
          getChipKey={getChipKey}
          handleChipToggle={handleChipToggle}
          handleChipRemove={handleChipRemove}
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={tagLogicType}
            onChange={(e) => setTagLogicType(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    tags2: () => (
      <FilterSection
        key="tags2"
        icon="fas fa-tag"
        title="Tags 2"
        selectedCount={selectedTags2.length}
        isExpanded={expandedSections.tags2}
        onExpandChange={handleSectionExpand('tags2')}
      >
        <CategorizedAutocomplete
          {...commonAutocompleteProps('tags2', availableTags, selectedTags2, setSelectedTags2)}
          autoHighlight
          section="tags2"
          getChipKey={getChipKey}
          handleChipToggle={handleChipToggle}
          handleChipRemove={handleChipRemove}
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={tagLogicType2}
            onChange={(e) => setTagLogicType2(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    genres: () => (
      <FilterSection
        key="genres"
        icon="tim-icons icon-world"
        title="Genres"
        selectedCount={selectedGenres.length}
        isExpanded={expandedSections.genres}
        onExpandChange={handleSectionExpand('genres')}
      >
        <Autocomplete
          {...commonAutocompleteProps('genres', availableGenres, selectedGenres, setSelectedGenres)}
          autoHighlight
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={genreLogicType}
            onChange={(e) => setGenreLogicType(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    keys: () => (
      <FilterSection
        key="keys"
        icon="tim-icons icon-key-25"
        title="Keys"
        selectedCount={selectedKeys.length}
        isExpanded={expandedSections.keys}
        onExpandChange={handleSectionExpand('keys')}
      >
        <Autocomplete
          {...commonAutocompleteProps('keys', availableKeys, selectedKeys, setSelectedKeys)}
          autoHighlight
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={keyLogicType}
            onChange={(e) => setKeyLogicType(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    bpm: () => (
      <FilterSection
        key="bpm"
        icon="tim-icons icon-heart-2"
        title="BPM"
        selectedCount={((selectedBPM) || (bpmRange.min && bpmRange.max)) ? 1 : 0}
        isExpanded={expandedSections.bpm}
        onExpandChange={handleSectionExpand('bpm')}
      >
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={bpmFilterType}
            onChange={handleBpmFilterTypeChange}
          >
            <FormControlLabel
              value="exact"
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="Exact"
            />
            <FormControlLabel
              value="range"
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="Range"
            />
          </RadioGroup>
        </FormControl>

        {bpmFilterType === 'exact' ? (
          <Autocomplete
            value={selectedBPM}
            onChange={handleBPMChange}
            options={bpmOptions}
            getOptionLabel={(option) => option.label || ''}
            disableCloseOnSelect={!isEnterKeySelection}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setIsEnterKeySelection(true);
              }
            }}
            onClose={() => {
              // Reset enter key flag when dropdown closes
              if (isEnterKeySelection) {
                setTimeout(() => setIsEnterKeySelection(false), 100);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select BPM"
                className={classes.autocomplete}
              />
            )}
            ListboxProps={{
              style: {
                fontSize: 'var(--menu-font-size)'
              }
            }}
            renderOption={(option, { selected, focused }) => {
              const optionKey = `bpm_${option.id || option.value}`;
              const isHovered = hoveredOption === optionKey;
              
              return (
                <div 
                  style={{ 
                    backgroundColor: selected
                      ? 'var(--accent-1-color)'
                      : focused
                        ? 'rgba(255, 0, 0, 0.5)'
                        : 'transparent',
                    color: '#fff',
                    cursor: 'pointer',
                    borderRadius: selected ? '8px' : undefined,
                    padding: selected ? '4px 16px' : '4px 16px',
                    margin: selected ? '2px 0' : '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minWidth: '80%',
                    transition: 'background 0.15s, color 0.15s',
                    position: 'relative'
                  }}
                  onMouseEnter={() => setHoveredOption(optionKey)}
                  onMouseLeave={() => setHoveredOption(null)}
                >
                  <span>{option.label}</span>
                  {isHovered && (
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', height: '16px' }}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Add the BPM as included (same as clicking on the item)
                          setSelectedBPM(option);
                        }}
                        style={{ 
                          color: 'rgba(255, 255, 255, 0.5)', 
                          fontSize: '18px', 
                          fontWeight: 'bold',
                          lineHeight: '1',
                          transition: 'color 0.15s ease',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          border: 'none',
                          padding: '0 4px',
                          minWidth: '16px',
                          height: '16px'
                        }}
                        onMouseEnter={(e) => e.target.style.color = 'rgba(255, 255, 255, 1)'}
                        onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.5)'}
                        title="Add as included BPM"
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              );
            }}
            autoHighlight
          />
        ) : (
          <div style={{ display: 'flex', gap: '16px' }}>
            <TextField
              variant="outlined"
              placeholder="Min BPM"
              value={bpmRange.min === null || bpmRange.min === undefined ? '' : bpmRange.min}
              onChange={handleBpmRangeChange('min')}
              className={classes.autocomplete}
              style={{ flex: 1 }}
            />
            <TextField
              variant="outlined"
              placeholder="Max BPM"
              value={bpmRange.max === null || bpmRange.max === undefined ? '' : bpmRange.max}
              onChange={handleBpmRangeChange('max')}
              className={classes.autocomplete}
              style={{ flex: 1 }}
            />
          </div>
        )}
      </FilterSection>
    ),
    date: () => (
      <FilterSection
        icon="fas fa-calendar"
        title="Date"
        selectedCount={dateRange.from || dateRange.to ? 1 : 0}
        isExpanded={expandedSections.date}
        onExpandChange={handleSectionExpand('date')}
      >
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={dateFilterType}
            onChange={handleDateFilterTypeChange}
          >
            <FormControlLabel
              value="completed"
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="Completed"
            />
            <FormControlLabel
              value="expires"
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="Expires"
            />
          </RadioGroup>
        </FormControl>

        <div style={{ display: 'flex', gap: '16px' }}>
          <TextField
            variant="outlined"
            type="date"
            value={dateRange.from ? moment(dateRange.from).format('YYYY-MM-DD') : ''}
            onChange={handleDateRangeChange('from')}
            className={classes.autocomplete}
            style={{ flex: 1 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            variant="outlined"
            type="date"
            value={dateRange.to ? moment(dateRange.to).format('YYYY-MM-DD') : ''}
            onChange={handleDateRangeChange('to')}
            className={classes.autocomplete}
            style={{ flex: 1 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </div>
      </FilterSection>
    ),
    license: () => (
      <FilterSection
        key="license"
        icon="fas fa-certificate"
        title="License"
        selectedCount={licenseFilter !== 'all' ? 1 : 0}
        isExpanded={expandedSections.license}
        onExpandChange={handleSectionExpand('license')}
      >
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={licenseFilter}
            onChange={(e) => {
              setLicenseFilter(e.target.value);
              if (onFiltersChange) {
                onFiltersChange({
                  ...prevFiltersRef.current,
                  license: e.target.value === 'all' ? null : { value: e.target.value === 'licensed' }
                });
              }
            }}
          >
            <FormControlLabel
              value="all"
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="All"
            />
            <FormControlLabel
              value="licensed"
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="Licensed"
            />
            <FormControlLabel
              value="unlicensed"
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="Unlicensed"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    composers: () => (
      <FilterSection
        key="composers"
        icon="tim-icons icon-single-copy-04"
        title="Composers"
        selectedCount={selectedComposers.length}
        isExpanded={expandedSections.composers}
        onExpandChange={handleSectionExpand('composers')}
      >
        <Autocomplete
          {...commonAutocompleteProps('composers', availableComposers, selectedComposers, setSelectedComposers)}
          autoHighlight
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={composerLogicType}
            onChange={(e) => setComposerLogicType(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    sessionArtists: () => (
      <FilterSection
        key="sessionArtists"
        icon="tim-icons icon-single-02"
        title="Session Artists"
        selectedCount={selectedSessionArtists.length}
        isExpanded={expandedSections.sessionArtists}
        onExpandChange={handleSectionExpand('sessionArtists')}
      >
        <Autocomplete
          {...commonAutocompleteProps('sessionArtists', availableSessionArtists, selectedSessionArtists, setSelectedSessionArtists)}
          autoHighlight
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={sessionArtistLogicType}
            onChange={(e) => setSessionArtistLogicType(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    clients: () => (
      <FilterSection
        key="clients"
        icon="fas fa-briefcase"
        title="Clients"
        selectedCount={selectedClients.length}
        isExpanded={expandedSections.clients}
        onExpandChange={handleSectionExpand('clients')}
      >
        <Autocomplete
          {...commonAutocompleteProps('clients', availableClients, selectedClients, setSelectedClients)}
          autoHighlight
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={clientLogicType}
            onChange={(e) => setClientLogicType(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    agencies: () => (
      <FilterSection
        key="agencies"
        icon="fas fa-building"
        title="Agencies"
        selectedCount={selectedAgencies.length}
        isExpanded={expandedSections.agencies}
        onExpandChange={handleSectionExpand('agencies')}
      >
        <Autocomplete
          {...commonAutocompleteProps('agencies', availableAgencies, selectedAgencies, setSelectedAgencies)}
          autoHighlight
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={agencyLogicType}
            onChange={(e) => setAgencyLogicType(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    producers: () => (
      <FilterSection
        key="producers"
        icon="tim-icons icon-notes"
        title="Producers"
        selectedCount={selectedProducers.length}
        isExpanded={expandedSections.producers}
        onExpandChange={handleSectionExpand('producers')}
      >
        <Autocomplete
          {...commonAutocompleteProps('producers', availableProducers, selectedProducers, setSelectedProducers)}
          autoHighlight
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={producerLogicType}
            onChange={(e) => setProducerLogicType(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    creativeDirectors: () => (
      <FilterSection
        key="creativeDirectors"
        icon="tim-icons icon-single-02"
        title="Creative Directors"
        selectedCount={selectedCreativeDirectors.length}
        isExpanded={expandedSections.creativeDirectors}
        onExpandChange={handleSectionExpand('creativeDirectors')}
      >
        <Autocomplete
          {...commonAutocompleteProps('creativeDirectors', availableCreativeDirectors, selectedCreativeDirectors, setSelectedCreativeDirectors)}
          autoHighlight
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={creativeDirectorLogicType}
            onChange={(e) => setCreativeDirectorLogicType(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
    studios: () => (
      <FilterSection
        key="studios"
        icon="fas fa-microphone"
        title="Studios"
        selectedCount={selectedStudios.length}
        isExpanded={expandedSections.studios}
        onExpandChange={handleSectionExpand('studios')}
      >
        <Autocomplete
          {...commonAutocompleteProps('studios', availableStudios, selectedStudios, setSelectedStudios)}
          autoHighlight
        />
        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            row
            value={studioLogicType}
            onChange={(e) => setStudioLogicType(e.target.value)}
          >
            <FormControlLabel
              value={LOGIC_TYPES.OR}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="OR"
            />
            <FormControlLabel
              value={LOGIC_TYPES.AND}
              control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
              label="AND"
            />
          </RadioGroup>
        </FormControl>
      </FilterSection>
    ),
  };

  return (
    <div className={classes.root}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Filters</h2>
        <div className={classes.buttonContainer}>
          <Button
            variant="contained"
            onClick={handleTestTagFilter}
            className="btn-primary-custom"
          >
            Tags
          </Button>
          <Button
            variant="contained"
            onClick={handleTestGenreFilter}
            className="btn-primary-custom"
          >
            Genre
          </Button>
          <Button
            variant="contained"
            onClick={handleTestBPMFilter}
            className="btn-primary-custom"
          >
            BPM
          </Button>
          {filterVisibility.creativeDirectors && (
            <Button
              variant="contained"
              onClick={handleTestCreativeDirectorFilter}
              className="btn-primary-custom"
            >
              CD
            </Button>
          )}
          {filterVisibility.composers && (
            <Button
              variant="contained"
              onClick={handleTestComposerFilter}
              className="btn-primary-custom"
            >
              Composer
            </Button>
          )}
          {filterVisibility.date && (
            <Button
              variant="contained"
              onClick={handleTestDateFilter}
              className="btn-primary-custom"
            >
              Date
            </Button>
          )}
          {filterVisibility.license && (
            <Button
              variant="contained"
              onClick={handleTestLicenseFilter}
              className="btn-primary-custom"
            >
              License
            </Button>
          )}
          <Button
            variant="contained"
            onClick={clearAllFilters}
            className="btn-danger-custom"
          >
            Clear
          </Button>
        </div>
      </div>

      {FILTER_ORDER.map(
        (filterKey) => filterVisibility[filterKey] && filterRenderers[filterKey] && (
          <div key={filterKey}>
            {filterRenderers[filterKey]()}
          </div>
        )
      )}
    </div>
  );
};

export default withFirebase(TestMUITags); 